// Which controls a call-out row offers, and which one is the visible primary (Session AEW).
//
// Pure, so the row can be reasoned about and TESTED rather than trusted. The important property
// this exists to protect is that moving five controls into an overflow menu removed none of them:
// availableActions is the same set before and after, and a test asserts exactly that against the
// pre-AEW guards.
//
// Why a primary at all: the Actions column was 97.5px holding 451px of buttons, which is not a
// width problem but a count problem (six labelled buttons plus eight data columns do not fit in
// the 974px this table gets at 1280px). The shared OverflowMenu's own contract is "secondary and
// destructive actions live here so each section keeps exactly one visible primary", so the row
// follows it.

export interface CalloutRowState {
  isAdmin: boolean;
  // Session ARA (S-7), D57: whether this user may assign or reassign an engineer to a call-out --
  // admin, or office since the Director's 23 September 2026 dispatch ruling ("the office role may
  // send an engineer to a call-out", docs/ELE-V2-DIRECTOR-DECISIONS-2026-07-19.md D57). A SEPARATE
  // field from isAdmin, deliberately: isAdmin alone still gates Delete below, which D57 never
  // touched, so widening isAdmin itself would have silently opened hard-delete to office too.
  canDispatch: boolean;
  cancelled: boolean; // status === "cancelled"
  cancellable: boolean; // not cancelled and not complete: still an open call-out
  hasEngineer: boolean; // an engineer is assigned
  hasJobSheet: boolean; // a visit record exists, so there may be photos
}

// SCOPE (stated since Session AHJ, when it stopped being true by accident): this models the
// call-out LIFECYCLE controls. It does NOT model the attendance-report controls beside them
// (Report, and since AHJ Open PDF, regenerate and Correct reference), which key off the report's
// own state rather than the call-out's status and are rendered from the row directly.
//
// That mattered: the row used to render the whole menu only when THIS set was non-empty, so an
// office user looking at a cancelled call-out saw no menu at all and a report already issued on it
// could neither be opened nor corrected. The menu now renders unconditionally; this set decides
// only which of the lifecycle controls appear in it.

// Every lifecycle control the row can offer, whatever its position. The names match the labels.
export type CalloutAction = "assign" | "reassign" | "charge" | "photos" | "complete" | "cancel" | "delete";

export interface CalloutRowActions {
  // The one control shown outside the menu. Null on a row with nothing primary to do (a completed
  // or cancelled call-out), which then shows the menu alone.
  primary: "assign" | "complete" | null;
  menu: CalloutAction[];
  all: CalloutAction[];
}

export function calloutRowActions(state: CalloutRowState): CalloutRowActions {
  const { isAdmin, canDispatch, cancelled, cancellable, hasEngineer, hasJobSheet } = state;

  // The full set. Assign and Reassign are one control wearing two labels, exactly as before AEW.
  // Session ARA (S-7): the assign/reassign guard reads canDispatch, not isAdmin, since D57 opened it
  // to office too; Delete stays on isAdmin, untouched by D57.
  const all: CalloutAction[] = [];
  if (canDispatch && cancellable) all.push(hasEngineer ? "reassign" : "assign");
  if (!cancelled) all.push("charge");
  if (hasJobSheet) all.push("photos");
  if (cancellable) all.push("complete");
  if (cancellable) all.push("cancel");
  if (isAdmin) all.push("delete");

  // The primary follows the workflow rather than being fixed: an unassigned call-out needs an
  // engineer before anything else, an assigned one needs completing. A user who cannot dispatch at
  // all (D57: neither admin nor office) cannot assign, so completing is always their primary while
  // the call-out is open.
  const primary: "assign" | "complete" | null = !cancellable
    ? null
    : canDispatch && !hasEngineer
      ? "assign"
      : "complete";

  return { primary, menu: all.filter((a) => a !== primary), all };
}
